import { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Headless from '@headlessui/react';
import { X, Phone } from 'lucide-react';
import { getEnabledTourSections, TourSection } from './welcomeTourData';
import WelcomeTourSidebar from './WelcomeTourSidebar';
import WelcomeTourContent from './WelcomeTourContent';

interface WelcomeTourDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  bookingUrl?: string;
}

const WelcomeTourDialog: FC<WelcomeTourDialogProps> = ({
  open,
  onClose,
  onComplete,
  bookingUrl,
}) => {
  const { t } = useTranslation('welcomeTour');
  const sections = useMemo(() => getEnabledTourSections(), []);
  const [activeSectionId, setActiveSectionId] = useState<string>(
    sections[0]?.id ?? ''
  );

  const activeSection: TourSection | undefined = useMemo(
    () => sections.find((s) => s.id === activeSectionId),
    [sections, activeSectionId]
  );

  const handleBookCall = () => {
    if (bookingUrl) {
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/40 transition duration-150 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in dark:bg-zinc-950/60"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Headless.DialogPanel
            transition
            className="relative flex w-full max-w-5xl h-[680px] rounded-xl bg-white shadow-2xl ring-1 ring-zinc-950/10 dark:bg-zinc-900 dark:ring-white/10 transition duration-150 data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 rounded-lg p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
              aria-label={t('TOUR_CLOSE')}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Left sidebar */}
            <WelcomeTourSidebar
              sections={sections}
              activeSectionId={activeSectionId}
              onSelectSection={setActiveSectionId}
            />

            {/* Right content area */}
            <div className="flex flex-1 flex-col overflow-hidden min-w-0">
              {activeSection && (
                <WelcomeTourContent section={activeSection} />
              )}

              {/* Footer bar */}
              <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-700 px-5 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-br-xl shrink-0">
                {bookingUrl ? (
                  <button
                    onClick={handleBookCall}
                    className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {t('TOUR_BOOK_SETUP_CALL')}
                  </button>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
                  >
                    {t('TOUR_SKIP')}
                  </button>

                  <button
                    onClick={onComplete}
                    className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    {t('TOUR_GET_STARTED')}
                  </button>
                </div>
              </div>
            </div>
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
};

export default WelcomeTourDialog;
